import { useCallback, useEffect, useMemo, useState } from 'react';
import { FEATURE_ACCESS_ATTRIBUTE_MAP } from 'config';
import {
  fetchOrgFeatureAccess,
  updateOrgFeatureAccess
} from 'services/featureAccess';

const EMPTY_FEATURE_ACCESS = Object.keys(FEATURE_ACCESS_ATTRIBUTE_MAP).reduce(
  (acc, key) => ({
    ...acc,
    [key]: false
  }),
  {}
);

const useOrganizationFeatureAccess = organizationName => {
  const [state, setState] = useState({
    organization: organizationName || null,
    attributes: {},
    features: EMPTY_FEATURE_ACCESS,
    pending: false,
    error: null
  });

  const refresh = useCallback(async () => {
    if (!organizationName) {
      const nextState = {
        organization: null,
        attributes: {},
        features: EMPTY_FEATURE_ACCESS,
        pending: false,
        error: null
      };
      setState(nextState);
      return nextState;
    }

    setState(prevState => ({
      ...prevState,
      pending: true,
      error: null
    }));

    try {
      const nextState = await fetchOrgFeatureAccess(organizationName);
      const resolvedState = {
        ...nextState,
        pending: false,
        error: null
      };
      setState(resolvedState);
      return resolvedState;
    } catch (error) {
      const nextState = {
        organization: organizationName,
        attributes: {},
        features: EMPTY_FEATURE_ACCESS,
        pending: false,
        error: error?.message || 'Failed to load organization features.'
      };
      setState(nextState);
      return nextState;
    }
  }, [organizationName]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const updateFeatures = useCallback(
    async featureUpdates => {
      if (!organizationName) {
        throw new Error('No organization selected.');
      }

      setState(prevState => ({
        ...prevState,
        pending: true,
        error: null
      }));

      try {
        const nextState = await updateOrgFeatureAccess(
          organizationName,
          featureUpdates
        );
        const resolvedState = {
          ...nextState,
          pending: false,
          error: null
        };
        setState(resolvedState);
        return resolvedState;
      } catch (error) {
        setState(prevState => ({
          ...prevState,
          pending: false,
          error: error?.message || 'Failed to save organization features.'
        }));
        throw error;
      }
    },
    [organizationName]
  );

  return useMemo(
    () => ({
      organization: state.organization,
      attributes: state.attributes,
      features: state.features,
      pending: state.pending,
      error: state.error,
      refresh,
      updateFeatures
    }),
    [
      refresh,
      state.attributes,
      state.error,
      state.features,
      state.organization,
      state.pending,
      updateFeatures
    ]
  );
};

export default useOrganizationFeatureAccess;
